import { Injectable } from "@angular/core";
import { LoadingController, ToastController } from "@ionic/angular";
import { Strings } from "../classes/messages";
import { AuthenticationService } from "./authentication.service";
import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { Theme } from "../models/theme.model";
import { catchError, first, map } from "rxjs/operators";
import { FaceDetectorService } from "./face-detector.service";
import { TranslateService } from "@ngx-translate/core";
import { LicenseValidationService } from "./license-validation.service";
import { StorageService } from "./storage.service";
import { LoggingService } from "./logging.service";
import { TraceIdService } from "./tools/trace-id.service";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: "root",
})
export class AppService {
    private $theme: Theme;

    private _enableStepStorageSessionKYC: boolean;

    private _enableStepExtractedDataSessionKYC: boolean;

    private _documentRetryAttempts: number = 3;

    private _livenessRetryAttempts: number = 3;

    get enableStepStorageSessionKYC(): boolean {
        return this._enableStepStorageSessionKYC;
    }

    get enableStepExtractedDataSessionKYC(): boolean {
        return this._enableStepExtractedDataSessionKYC;
    }

    get documentRetryAttempts(): number {
        return this._documentRetryAttempts;
    }

    get livenessRetryAttempts(): number {
        return this._livenessRetryAttempts;
    }

    get theme(): Theme {
        return this.$theme;
    }

    private themeSubject = new BehaviorSubject<Theme>(this.theme);

    constructor(
        private authSrv: AuthenticationService,
        private faceDetectorSrv: FaceDetectorService,
        private toastCtrl: ToastController,
        private loadingCtrl: LoadingController,
        private translate: TranslateService,
        private licenseValidationSrv: LicenseValidationService,
        private storageSrv: StorageService,
        private loggingService: LoggingService,
        private traceIdService: TraceIdService,
    ) {
        this.setup();
    }

    private setup(): void {
        this.setupVars();
    }

    private setupVars(): void {
        this.$theme = null;
        this.themeSubject.next(this.$theme);
    }

    public async initialize(): Promise<void> {
        this.faceDetectorSrv
            .loadModels()
            .then()
            .catch((e) => {
                throw new Error(e);
            });

        // Validate license automatically if enabled
        if (environment.license.enabled) {
            await this.validateLicenseAutomatically();
        }

        await this.loadTheme();
        await this.authSrv.factoryLoadUserSession();
    }

    /**
     * Automatically validate license in background
     * Looks for license in URL params or stored license
     */
    private async validateLicenseAutomatically(): Promise<void> {
        try {
            // Check if there's already a valid license session
            const existingSession = this.licenseValidationSrv.getLicenseSession();
            if (existingSession) {
                this.loggingService.log(
                    "Valid license session found, using existing session",
                    false,
                    {
                        session_id: existingSession.sessionId,
                        expires_at: existingSession.expiresAt.format(),
                    },
                );
                // Use existing session
                this.authSrv.createSessionFromLicense(existingSession);
                return;
            }

            // Try to get license from URL params
            const urlParams = new URLSearchParams(window.location.search);
            const licenseFromUrl = urlParams.get("license") || urlParams.get("lic");

            // Try to get license from stored value
            const storedLicense = this.storageSrv.getLocalStorageDecrypted<string>("_cap_license_key");

            const licenseKey = licenseFromUrl || storedLicense;

            if (!licenseKey) {
                // Fallback: use persisted trace ID to re-validate license (e.g., after reload)
                const traceId = this.traceIdService.getSessionTraceId();
                if (traceId) {
                    this.loggingService.log(
                        "Re-validating license with persisted trace ID",
                        false,
                        { traceId },
                    );
                    await this.authSrv.validateLicenseWithTraceId(traceId);
                    return;
                }

                this.loggingService.log(
                    "No license key found in URL or storage, skipping automatic validation",
                    false,
                );
                return;
            }

            this.loggingService.log(
                "Validating license automatically in background",
                false,
                { license: licenseKey },
            );

            // Validate license
            const licenseSession = await firstValueFrom(
                this.licenseValidationSrv.validateLicense(licenseKey)
            );

            // Store license key for future use
            this.storageSrv.setLocalStorageEncrypted("_cap_license_key", licenseKey);

            // Create authentication session from license
            this.authSrv.createSessionFromLicense(licenseSession);

            this.loggingService.log(
                "License validated automatically",
                false,
                {
                    session_id: licenseSession.sessionId,
                    jti: licenseSession.jti,
                },
            );
        } catch (error) {
            this.loggingService.error(
                "Automatic license validation failed",
                false,
                error,
            );
            // Don't block app initialization if license validation fails
            // User can still proceed with the normal flow
        }
    }

    getTheme$(): Observable<Theme> {
        return this.themeSubject.asObservable();
    }

    async loadTheme(themeValue?: any): Promise<void> {
        if (themeValue) {
            themeValue = Theme.from({
                colors: {
                    primary:
                        themeValue.customizable?.colors?.primary || "#262E48",
                    secondary:
                        themeValue.customizable?.colors?.secondary || "#3B82F6",
                },
                pictures: {
                    logo:
                        themeValue.customizable?.images?.logo?.original ||
                        "/assets/img/JAAK_Azul.png",
                },
                countryList: themeValue.customizable?.countryList || [],
                customText: themeValue.customizable?.products?.traditionalKYC?.text,
            });
        }
        const theme = themeValue || await firstValueFrom(this.getTheme().pipe(first()));
        if (!theme) {
            return;
        }

        const body = document.querySelector("body");
        const contrast = this.invertColor(theme.colors.primary);
        if (contrast !== "#FFFFFF") {
            body.style.setProperty("--ion-color-primary-contrast", contrast);
            body.style.setProperty("--contrast-primary-color", contrast);
        }
        body.style.setProperty("--ion-color-primary", theme.colors.primary);
        body.style.setProperty("--primary-color", theme.colors.primary);
        body.style.setProperty("--secondary-color", theme.colors.secondary);
        body.style.setProperty("--ion-color-color", theme.colors.secondary);
        const favicon: HTMLLinkElement =
            document.querySelector("link[rel~='icon']");
        favicon.href = theme.pictures.logo;

        this.$theme = theme;
        this.themeSubject.next(this.$theme);
    }

    invertColor(hex: string, bw: boolean = true) {
        if (hex.indexOf("#") === 0) {
            hex = hex.slice(1);
        }
        // convert 3-digit hex to 6-digits.
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        if (hex.length !== 6) {
            throw new Error("Invalid HEX color.");
        }
        let r: number | string = parseInt(hex.slice(0, 2), 16);
        let g: number | string = parseInt(hex.slice(2, 4), 16);
        let b: number | string = parseInt(hex.slice(4, 6), 16);
        if (bw) {
            return r * 0.299 + g * 0.587 + b * 0.114 > 186
                ? "#323232"
                : "#FFFFFF";
        }
        // invert color components
        r = (255 - r).toString(16);
        g = (255 - g).toString(16);
        b = (255 - b).toString(16);
        // pad each with zeros and return
        return "#" + this.padZero(r) + this.padZero(g) + this.padZero(b);
    }

    padZero(str: string, len = 2) {
        const zeros = new Array(len).join("0");
        return (zeros + str).slice(-len);
    }

    private getTheme(): Observable<Theme> {
        return this.authSrv.companyMe().pipe(
            map((theme) => {
                this._enableStepStorageSessionKYC = theme.customizable.enableStepStorageSessionKYC;
                this._enableStepExtractedDataSessionKYC = theme.customizable.enableStepExtractedDataSessionKYC;

                const scoringSteps = theme.customizable?.products?.traditionalKYC?.scoring?.steps;
                if (Array.isArray(scoringSteps)) {
                    const documentStep = scoringSteps.find((s: any) => s.key === "liveness-document");
                    this._documentRetryAttempts = documentStep?.retryAttempts ?? 3;

                    const livenessStep = scoringSteps.find((s: any) => s.key === "liveness");
                    this._livenessRetryAttempts = livenessStep?.retryAttempts ?? 3;
                }

                return Theme.from({
                    colors: {
                        primary:
                            theme.customizable?.colors?.primary || "#262E48",
                        secondary:
                            theme.customizable?.colors?.secondary || "#3B82F6",
                    },
                    pictures: {
                        logo:
                            theme.customizable?.images?.logo?.original ||
                            null,
                    },
                    customText: theme.customizable?.products?.traditionalKYC?.text,
                    countryList: theme.customizable?.countryList || [],
                });
            }),
            catchError(() =>
                of(
                    Theme.from({
                        colors: {
                            primary: "#262E48",
                            secondary: "#3B82F6",
                        },
                        pictures: {
                            logo: null,
                        },
                        customText: null,
                        countryList: [],
                    }),
                ),
            ),
        );
    }

    public async showToast(
        message: string,
        duration = 5000,
        color = "danger",
        position: "top" | "bottom" | "middle" = "top",
    ): Promise<void> {
        const toast = await this.toastCtrl.create({
            message,
            color,
            duration,
            position,
        });
        return toast.present();
    }

    public async showLoading(
        message = Strings.pleaseAwaitMessage,
        mode: "ios" | "md" = "ios",
    ): Promise<void> {
        const loading = await this.loadingCtrl.create({
            message: this.translate.instant(message),
            mode,
        });
        return loading.present();
    }

    public dismissLoading(): Promise<boolean> {
        return this.loadingCtrl.dismiss();
    }
}

export function appInitializer(appSrv: AppService): () => Promise<void> {
    return async () => {
        await appSrv.initialize();
    };
}
